import React, { useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { authAPI, persistAuthSession, type LoginUser } from "@/lib/api";
import { applyFailedMfa } from "./loginErrors";
import { OtpSixBoxes } from "./OtpSixBoxes";

interface Props {
  mfaToken: string;
  onComplete: (user: LoginUser, token: string) => void;
  onBack: () => void;
}

export function MfaChallenge({ mfaToken, onComplete, onBack }: Props) {
  const [totp, setTotp] = useState("");
  const [backup, setBackup] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = backup.trim() || totp.replace(/\D/g, "");
    if (!code) {
      setError("Ingresá el código de 6 dígitos o un código de respaldo.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await authAPI.mfaVerify(mfaToken, code, rememberDevice);
      persistAuthSession(result);
      onComplete(result.user, result.token);
    } catch (err: unknown) {
      const failed = applyFailedMfa(err);
      setTotp(failed.code);
      setBackup("");
      setError(failed.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Código de authenticator</h2>
      <p className="text-sm text-gray-600">
        Ingresá el código de 6 dígitos de tu app.
      </p>
      <OtpSixBoxes
        idPrefix="login-otp"
        value={totp}
        onChange={(val) => {
          setTotp(val);
          setError("");
        }}
        disabled={loading}
      />
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          disabled={loading}
          className="mt-1"
        />
        Recordar este dispositivo 24 horas
      </label>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          O código de respaldo
        </label>
        <input
          type="text"
          autoComplete="off"
          value={backup}
          onChange={(e) => {
            setBackup(e.target.value);
            setError("");
          }}
          disabled={loading}
          placeholder="XXXX-XXXX"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] text-gray-900 text-sm"
        />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>No se pudo verificar</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack} className="flex-1">
          Volver
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#d97706] hover:bg-[#b86607] text-white"
        >
          {loading ? "Verificando..." : "Verificar"}
        </Button>
      </div>
    </form>
  );
}
